// AboutUsPage.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import "./AboutUsPage.css";
import workspace from "../assets/images/workspace.jpg";
import flexibleWorkspaceIcon from "../assets/images/simple-flexible-workspace-office-icon.svg";
import customBuildoutIcon from "../assets/images/office-custom-buildouts-icon-no-text.svg";
import globalNetworkIcon from "../assets/images/global-network-small.svg";
import technologyIcon from "../assets/images/technology-icon-.svg";

// Define TypeScript interfaces
interface SolutionCardProps {
  title: string;
  description: string;
  icon: string;
}

interface TestimonialProps {
  quote: string;
  author: string;
  position: string;
  company: string;
}

const AboutUsPage: React.FC = () => {
  const navigate = useNavigate();
  const solutions: SolutionCardProps[] = [
    {
      title: "Flexible Workspaces",
      description:
        "Ready-to-use offices with flexible terms that scale with your business.",
      icon: flexibleWorkspaceIcon,
    },
    {
      title: "Custom Buildouts",
      description:
        "Tailored office designs that reflect your brand and culture.",
      icon: customBuildoutIcon,
    },
    {
      title: "Global Network",
      description: "Access to workspaces in hundreds of locations worldwide.",
      icon: globalNetworkIcon,
    },
    {
      title: "Workspace Technology",
      description:
        "Integrated tech solutions for seamless workplace management.",
      icon: technologyIcon,
    },
  ];

  const SolutionsGrid: React.FC = () => (
    <section className="solutions-section">
      <h2>Comprehensive workplace solutions</h2>
      <div className="solutions-grid">
        {solutions.map((solution, index) => (
          <SolutionCard key={index} {...solution} />
        ))}
      </div>
    </section>
  );

  const SolutionCard: React.FC<SolutionCardProps> = ({
    title,
    description,
    icon,
  }) => (
    <div className="solution-card">
      <img src={icon} alt={`${title} icon`} className="solution-icon" />
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );

  const HeroSection: React.FC = () => (
    <section className="hero-section">
      <div className="hero-content">
        <h1 className="">Workspace solutions for all businesses</h1>
        <p>
          AirArrange provides flexible, scalable workplace solutions to help
          your business adapt and grow.
        </p>
        <div className="hero-buttons">
          <button className="secondary-button" onClick={handleContactSales}>
            Contact Sales
          </button>
          <button className="secondary-button" onClick={handleBrowseLocations}>
            Browse Locations
          </button>
        </div>
      </div>
      <div className="hero-image">
        <img src={workspace} alt="Workspace workspace" />
      </div>
    </section>
  );
  const CTASection: React.FC = () => (
    <section className="cta-section">
      <div className="cta-content">
        <h2>Can't find a solution for your workspace needs?</h2>
        <p>
          Speak with our Workspace team to find the perfect solution for your
          organization.
        </p>
        <button className="secondary-button" onClick={handleContactSales}>
          Contact Our Sales Team
        </button>
      </div>
    </section>
  );
  const StatsSection: React.FC = () => (
    <section className="stats-section">
      <div className="stats-container">
        <div className="stat-item">
          <h3>700+</h3>
          <p>Locations worldwide</p>
        </div>
        <div className="stat-item">
          <h3>40%</h3>
          <p>Fortune 500 companies work with us</p>
        </div>
        <div className="stat-item">
          <h3>90%</h3>
          <p>Of members would recommend WeWork</p>
        </div>
      </div>
    </section>
  );
  const testimonials: TestimonialProps[] = [
    {
      quote:
        "WeWork's Workspace solution allowed us to quickly establish a presence in new markets with minimal capital expenditure.",
      author: "Sarah Johnson",
      position: "Global Head of Real Estate",
      company: "TechCorp",
    },
    {
      quote:
        "The flexibility of WeWork's spaces has been instrumental in supporting our hybrid work model and employee satisfaction.",
      author: "Michael Chen",
      position: "CFO",
      company: "InnovateCo",
    },
    {
      quote:
        "From day one, WeWork understood our needs and delivered spaces that truly represent our brand and values.",
      author: "Emma Rodriguez",
      position: "VP of Workplace Experience",
      company: "DesignForward",
    },
  ];

  const TestimonialsSection: React.FC = () => (
    <section className="testimonials-section">
      <h2>Trusted by leading enterprises</h2>
      <div className="testimonials-grid">
        {testimonials.map((testimonial, index) => (
          <TestimonialCard key={index} {...testimonial} />
        ))}
      </div>
    </section>
  );

  const TestimonialCard: React.FC<TestimonialProps> = ({
    quote,
    author,
    position,
    company,
  }) => (
    <div className="testimonial-card">
      <p className="quote">"{quote}"</p>
      <div className="author-info">
        <p className="author-name">{author}</p>
        <p className="author-position">
          {position}, {company}
        </p>
      </div>
    </div>
  );
  // Navigation handlers
  const handleContactSales = () => navigate("/contact");
  const handleBrowseLocations = () => navigate("/");

  return (
    <div className="Workspace-page">
      <HeroSection />
      <SolutionsGrid />
      <StatsSection />
      <TestimonialsSection />
      <CTASection />
    </div>
  );
};

export default AboutUsPage;
